import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { 
  Database, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  Search,
  Wrench,
  Trash2,
  Activity,
  Users
} from 'lucide-react';

/**
 * DatabaseMaintenanceCard - R-PAT Database maintenance tools for admins
 * Handles completion sync, incorrect completion cleanup, and simulation data fixes
 */
export const DatabaseMaintenanceCard = ({ api }) => {
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [simFixRunning, setSimFixRunning] = useState(false);
  const [simFixResult, setSimFixResult] = useState(null);
  const [simHealthRunning, setSimHealthRunning] = useState(false);
  const [simHealthResult, setSimHealthResult] = useState(null);

  const runCompletionSync = async (dryRun = true) => {
    setSyncRunning(true);
    setSyncResult(null);
    try {
      const response = await api.post(`/rpat/admin/fix-completion-sync?dry_run=${dryRun}`);
      setSyncResult(response.data);
      if (dryRun) {
        if (response.data.users_fixed > 0) {
          toast.info(`Found ${response.data.users_fixed} users with ${response.data.topics_added} missing completions. Click "Fix Now" to repair.`);
        } else {
          toast.success('All user data is consistent. No fixes needed.');
        }
      } else {
        if (response.data.users_fixed > 0) {
          toast.success(`Fixed ${response.data.users_fixed} users! ${response.data.topics_added} topic completions restored.`);
        } else {
          toast.success('All data is already consistent.');
        }
      }
    } catch (error) {
      toast.error('Failed to run completion sync');
      console.error(error);
    } finally {
      setSyncRunning(false);
    }
  };

  const runIncorrectCleanup = async (dryRun = true) => {
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const response = await api.post(`/rpat/admin/cleanup-incorrect-completions?dry_run=${dryRun}`);
      setCleanupResult(response.data);
      if (dryRun) {
        if (response.data.users_affected > 0) {
          toast.info(`Found ${response.data.users_affected} users with ${response.data.topics_removed} incorrect completions. Click "Clean Now" to fix.`);
        } else {
          toast.success('No incorrect completions found. Data is clean.');
        }
      } else {
        if (response.data.users_affected > 0) {
          toast.success(`Cleaned ${response.data.users_affected} users! ${response.data.topics_removed} incorrect completions removed.`);
        } else {
          toast.success('Data already clean.');
        }
      }
    } catch (error) {
      toast.error('Failed to run cleanup');
      console.error(error);
    } finally {
      setCleanupRunning(false);
    }
  };

  const checkSimulationHealth = async () => {
    setSimHealthRunning(true);
    setSimHealthResult(null);
    try {
      const response = await api.get('/rpat/phase4-simulations/admin/simulation-health');
      setSimHealthResult(response.data);
      const issues = response.data.issues;
      const totalIssues = issues.string_scores + issues.potentially_incomplete;
      if (totalIssues > 0) {
        toast.info(`Found ${totalIssues} potential issues. Click "Fix Simulation Data" to repair.`);
      } else {
        toast.success('All simulation data looks healthy!');
      }
    } catch (error) {
      toast.error('Failed to check simulation health');
      console.error(error);
    } finally {
      setSimHealthRunning(false);
    }
  };

  const fixSimulationData = async () => {
    setSimFixRunning(true);
    setSimFixResult(null);
    try {
      const response = await api.post('/rpat/phase4-simulations/admin/fix-simulation-data');
      setSimFixResult(response.data);
      if (response.data.details.total_fixed > 0) {
        toast.success(`Fixed ${response.data.details.total_fixed} simulations! Users' progress should now be accurate.`);
      } else {
        toast.success('All simulation data is already correct.');
      }
      // Refresh health check
      await checkSimulationHealth();
    } catch (error) {
      toast.error('Failed to fix simulation data');
      console.error(error);
    } finally {
      setSimFixRunning(false);
    }
  };

  return (
    <Card className="border-2 border-amber-500/20 bg-gradient-to-r from-amber-500/5 to-orange-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-amber-500" />
          R-PAT Database Maintenance
        </CardTitle>
        <CardDescription>
          Fix data inconsistencies for users experiencing phase unlock issues
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Fix Missing Completions */}
          <div className="space-y-4 p-4 rounded-lg border bg-background">
            <div>
              <Label className="text-base font-semibold flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Fix Missing Completions
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Adds topics to completed list if assessment was passed but not recorded. 
                Fixes phase unlock issues where completed phases still show locked.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => runCompletionSync(true)}
                disabled={syncRunning}
                variant="outline"
                className="flex-1"
                data-testid="sync-check-btn"
              >
                {syncRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Check Issues
              </Button>
              <Button 
                onClick={() => runCompletionSync(false)}
                disabled={syncRunning || !syncResult || syncResult.users_fixed === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="sync-fix-btn"
              >
                {syncRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wrench className="h-4 w-4 mr-2" />
                )}
                Fix Now
              </Button>
            </div>
            
            {syncResult && (
              <div className={`p-3 rounded-lg text-sm ${
                syncResult.users_fixed > 0 
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
              }`}>
                {syncResult.dry_run ? (
                  syncResult.users_fixed > 0 ? (
                    <p><strong>{syncResult.users_fixed}</strong> users with <strong>{syncResult.topics_added}</strong> missing completions found. Click Fix Now to repair.</p>
                  ) : (
                    <p>All user data is consistent. No fixes needed.</p>
                  )
                ) : (
                  <p>Fixed <strong>{syncResult.users_fixed}</strong> users. <strong>{syncResult.topics_added}</strong> completions restored.</p>
                )}
              </div>
            )}
          </div>

          {/* Remove Incorrect Completions */}
          <div className="space-y-4 p-4 rounded-lg border bg-background">
            <div>
              <Label className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Remove Incorrect Completions
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Removes topics that were incorrectly marked complete (e.g., from typing test only).
                Only removes if no legitimate completion evidence exists.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => runIncorrectCleanup(true)}
                disabled={cleanupRunning}
                variant="outline"
                className="flex-1"
                data-testid="cleanup-check-btn"
              >
                {cleanupRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Check Issues
              </Button>
              <Button 
                onClick={() => runIncorrectCleanup(false)}
                disabled={cleanupRunning || !cleanupResult || cleanupResult.users_affected === 0}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="cleanup-fix-btn"
              >
                {cleanupRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Clean Now
              </Button>
            </div>
            
            {cleanupResult && (
              <div className={`p-3 rounded-lg text-sm ${
                cleanupResult.users_affected > 0 
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
              }`}>
                {cleanupResult.dry_run ? (
                  cleanupResult.users_affected > 0 ? (
                    <p><strong>{cleanupResult.users_affected}</strong> users with <strong>{cleanupResult.topics_removed}</strong> incorrect completions found. Click Clean Now to remove.</p>
                  ) : (
                    <p>No incorrect completions found. Data is clean.</p>
                  )
                ) : (
                  <p>Cleaned <strong>{cleanupResult.users_affected}</strong> users. <strong>{cleanupResult.topics_removed}</strong> incorrect completions removed.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fix Simulation Progress - Full Width */}
        <div className="mt-6 space-y-4 p-4 rounded-lg border-2 border-cyan-500/30 bg-gradient-to-r from-cyan-500/5 to-blue-500/5">
          <div>
            <Label className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-cyan-500" />
              Fix Simulation Progress (Phase 4)
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Fixes simulation data issues that prevent Phase 4 progress from being tracked correctly.
              This repairs score type mismatches and ensures completed simulations are properly counted.
            </p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={checkSimulationHealth}
              disabled={simHealthRunning || simFixRunning}
              variant="outline"
              className="border-cyan-500/50"
              data-testid="sim-health-check-btn"
            >
              {simHealthRunning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Check Simulation Health
            </Button>
            <Button 
              onClick={fixSimulationData}
              disabled={simFixRunning || simHealthRunning}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="sim-fix-btn"
            >
              {simFixRunning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Fix Simulation Data
            </Button>
          </div>
          
          {/* Health Check Results */}
          {simHealthResult && (
            <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm space-y-3">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-emerald-500" />
                  <strong>{simHealthResult.healthy_stats?.users_with_passing_completions || 0}</strong> users with passing simulations
                </span>
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <strong>{simHealthResult.issues?.string_scores || 0}</strong> score type issues
                </span>
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <strong>{simHealthResult.issues?.potentially_incomplete || 0}</strong> status issues
                </span>
              </div>
              
              {/* Show ALL scenario types in database */}
              {simHealthResult.all_scenario_types && (
                <div className="text-xs">
                  <p className="font-medium text-muted-foreground mb-1">All scenario types in database:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(simHealthResult.all_scenario_types).map(([type, count]) => {
                      const isExpected = simHealthResult.expected_scenario_types?.includes(type);
                      const isOther = Boolean(simHealthResult.other_scenario_types?.[type]);
                      return (
                        <span 
                          key={type} 
                          className={`px-2 py-0.5 rounded text-xs ${
                            isExpected 
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' 
                              : isOther
                                ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                          }`}
                        >
                          {type}: {count} {!isExpected && !isOther && '⚠️'}
                        </span>
                      );
                    })}
                  </div>
                  {simHealthResult.other_scenario_types && Object.keys(simHealthResult.other_scenario_types).length > 0 && (
                    <p className="mt-1 text-muted-foreground" data-testid="sim-health-other-types">
                      Grey types are valid simulations that are not part of Phase 4 (for example Communication Coach). Nothing to fix.
                    </p>
                  )}
                </div>
              )}
              
              {/* Unrecognized scenario types warning */}
              {simHealthResult.unrecognized_scenario_types && Object.keys(simHealthResult.unrecognized_scenario_types).length > 0 && (
                <div className="p-2 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs">
                  <p className="font-semibold">⚠️ Unrecognized scenario types found!</p>
                  <p className="mt-1">
                    These simulations will not count towards Phase 4 progress: {' '}
                    {Object.entries(simHealthResult.unrecognized_scenario_types).map(([type, count]) => (
                      <span key={type} className="font-mono">{type} ({count})</span>
                    )).reduce((prev, curr) => [prev, ', ', curr])}
                  </p>
                  <p className="mt-1 text-xs opacity-80">
                    Expected types: {simHealthResult.expected_scenario_types?.join(', ')}
                  </p>
                </div>
              )}
              
              {simHealthResult.status_distribution && (
                <div className="text-xs text-muted-foreground">
                  Status breakdown: {Object.entries(simHealthResult.status_distribution).map(([k, v]) => `${k || 'null'}: ${v}`).join(', ')}
                </div>
              )}
            </div>
          )}
          
          {/* Fix Results */}
          {simFixResult && (
            <div className={`p-3 rounded-lg text-sm ${
              simFixResult.details?.total_fixed > 0 
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' 
                : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
            }`}>
              <p>
                Scanned <strong>{simFixResult.details?.total_scanned || 0}</strong> simulations. 
                Fixed <strong>{simFixResult.details?.total_fixed || 0}</strong> issues 
                ({simFixResult.details?.score_types_fixed || 0} score types, {simFixResult.details?.status_fixed || 0} status updates).
              </p>
              
              {/* Show scenario type distribution from fix */}
              {simFixResult.details?.scenario_type_distribution && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs font-medium">View scenario type distribution</summary>
                  <div className="mt-1 text-xs flex flex-wrap gap-1">
                    {Object.entries(simFixResult.details.scenario_type_distribution).map(([type, count]) => {
                      const isExpected = simFixResult.details.expected_scenario_types?.includes(type);
                      const isOther = simFixResult.details.other_scenario_types?.includes(type);
                      return (
                        <span 
                          key={type}
                          className={`px-2 py-0.5 rounded ${
                            isExpected 
                              ? 'bg-emerald-200 dark:bg-emerald-800' 
                              : isOther ? 'bg-slate-200 dark:bg-slate-700' : 'bg-red-200 dark:bg-red-800'
                          }`}
                        >
                          {type || 'null'}: {count}
                        </span>
                      );
                    })}
                  </div>
                </details>
              )}
              
              {simFixResult.details?.sample_issues?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs">View sample issues fixed</summary>
                  <ul className="mt-1 text-xs space-y-1">
                    {simFixResult.details.sample_issues.slice(0, 5).map((issue, idx) => (
                      <li key={idx}>• [{issue.scenario_type}] {issue.issue}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DatabaseMaintenanceCard;
